'use client'

import { useState, useEffect } from 'react'
import { X, User, LogOut, Settings, Wallet, Lock, RefreshCw } from 'lucide-react'
import { useFreemium } from '@/lib/freemium-context'
import { Z } from '@/lib/z-index'
import { FREE_LIMITS } from '@/lib/limits'
import { REFERENCE_PRICE_LABEL } from '@/lib/pricing-display'

interface UserInfo {
  email: string
  full_name?: string | null
}

interface RecentItem {
  id: string
  file_name: string
  statut: string
  created_at: string
}

/** Un paiement CONFIRMÉ, tel que servi par /api/payment/history (table `transactions`).
 *  `amount` est ici un montant en ARGENT, dans `currency` — à ne pas confondre
 *  avec le solde de crédits. */
interface PaymentRecord {
  id: string
  amount: number
  currency: string
  provider: string | null
  method: string | null
  created_at: string
}

interface UserPanelProps {
  user: UserInfo | null
  credits: number
  plan: string
  onLogout: () => void
  onOpenDashboard?: () => void
  onOpenAdmin?: () => void
}

function UsageBar({ value, max }: { value: number; max: number }) {
  const pct = max === 0 ? 100 : Math.min(Math.round((value / max) * 100), 100)
  const color = pct <= 20 ? '#EF4444' : pct <= 50 ? '#F59E0B' : '#1B4332'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 5, borderRadius: 3, background: '#E5E7EB', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width .3s' }} />
      </div>
      <span style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap', minWidth: 32, textAlign: 'right' }}>{value}/{max}</span>
    </div>
  )
}

function ModuleCard({ emoji, title, children }: { emoji: string; title: string; children: React.ReactNode }) {
  return (
    <div style={{ borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: 8 }}>
      <div style={{ padding: '8px 12px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13 }}>{emoji}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#374151' }}>{title}</span>
      </div>
      <div style={{ padding: '10px 12px' }}>{children}</div>
    </div>
  )
}

function LockedModuleCard({ emoji, title, onUpgrade }: { emoji: string; title: string; onUpgrade: () => void }) {
  return (
    <div style={{ borderRadius: 8, border: '1.5px solid #E5E7EB', overflow: 'hidden', marginBottom: 8, opacity: 0.9 }}>
      <div style={{ padding: '8px 12px', background: '#F3F4F6', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Lock size={12} color="#9CA3AF" />
        <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7280' }}>{emoji} {title}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: '#F0FAF4', color: '#1B4332' }}>PRO</span>
      </div>
      <div style={{ padding: '10px 12px' }}>
        <p style={{ fontSize: 12, color: '#6B7280', margin: '0 0 8px' }}>Ce module nécessite le Plan Pro.</p>
        <button
          onClick={onUpgrade}
          style={{ fontSize: 12, fontWeight: 600, color: '#1B4332', background: '#F0FAF4', border: '1px solid #c8e6d0', borderRadius: 6, padding: '5px 10px', cursor: 'pointer' }}
        >
          🚀 Passer au Pro
        </button>
      </div>
    </div>
  )
}

export default function UserPanel({ user, credits, plan, onLogout, onOpenDashboard, onOpenAdmin }: UserPanelProps) {
  const [open, setOpen] = useState(false)
  const [recentItems, setRecentItems] = useState<RecentItem[]>([])
  const [expiresAt, setExpiresAt] = useState<string | null>(null)
  const [paymentHistory, setPaymentHistory] = useState<PaymentRecord[]>([])
  const { limits, openUpgrade, isAdmin } = useFreemium()

  const isPro = plan === 'pro' || limits.is_pro
  const displayName = user?.full_name || user?.email?.split('@')[0] || 'Utilisateur'

  const maxCredits = isPro ? 1300 : 100
  const creditPct = Math.min(Math.round((credits / maxCredits) * 100), 100)
  const creditBtnColor = creditPct <= 20 ? '#EF4444' : creditPct <= 50 ? '#F59E0B' : '#1B4332'

  useEffect(() => {
    if (!open) return
    fetch('/api/transcriptions')
      .then(r => r.ok ? r.json() : { transcriptions: [] })
      .then((d: { transcriptions: RecentItem[] }) => setRecentItems((d.transcriptions ?? []).slice(0, 3)))
      .catch(() => {})
    fetch('/api/credits/check')
      .then(r => r.ok ? r.json() : null)
      .then((d: { expiresAt?: string | null } | null) => { if (d?.expiresAt) setExpiresAt(d.expiresAt) })
      .catch(() => {})
    // Historique des PAIEMENTS — `transactions`, la seule table portant un
    // montant en argent. L'appel précédent visait /api/credits/history et
    // filtrait sur `type='recharge'`, valeur qu'aucun code n'écrit : la section
    // ne s'affichait jamais.
    fetch('/api/payment/history')
      .then(r => r.ok ? r.json() : { payments: [] })
      .then((d: { payments?: PaymentRecord[] }) => setPaymentHistory((d.payments ?? []).slice(0, 3)))
      // BEST-EFFORT assumé (D-023) : l'échec est acceptable — seul l'historique
      // manque, le panneau reste utilisable ; aucune incohérence métier, cette
      // lecture ne conditionne ni crédits ni droits ; et faire échouer
      // l'ouverture du panneau pour un encart secondaire serait disproportionné.
      .catch(() => {})
  }, [open])

  if (!user) return null

  return (
    <>
      {/* ── Bouton flottant fixe ──────────────────────────────────────────────
          POSITION CONTRAINTE PAR LA BULLE DE SUPPORT. `ChatWidget` (monté
          globalement dans layout.tsx) occupe bottom:20/right:20 sur 56 px. Ce
          bouton était à bottom:24/right:24 : la bulle en recouvrait le tiers
          droit et, les deux se confondant visuellement, on cliquait sur le
          cercle. Le panneau est ainsi resté inaccessible depuis mai 2026
          (commit ee83e7a, arrivée du chat).

          Le décalage reste nécessaire, et c'est une question de GÉOMÉTRIE, non
          d'empilement : 92 = 20 (bas de la bulle) + 56 (sa hauteur) + 16
          (marge). Même au bon niveau, deux éléments superposés au même endroit
          resteraient indiscernables à l'œil. C'est ce composant qui se décale,
          pas le chat : la bulle est globale et présente sur toutes les pages,
          ce bouton n'existe que sur l'application.

          L'empilement, lui, est désormais réglé par l'échelle : `Z.floating`
          passe au-dessus de `Z.support` (dette n°8, `src/lib/z-index.ts`). */}
      <button
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed', bottom: 92, right: 24, zIndex: Z.floating,
          background: '#1B4332', color: '#fff', border: 'none',
          borderRadius: 12, padding: '10px 16px', cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(27,67,50,0.35)',
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4,
          minWidth: 164,
        }}
        aria-label="Ouvrir mon espace"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600 }}>
            <User size={14} />
            {displayName}
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: isPro ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.15)', letterSpacing: '0.04em' }}>
            {isAdmin ? 'Admin' : isPro ? 'Pro ⭐' : 'Free'}
          </span>
        </div>
        {/* Credit bar */}
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flex: 1, height: 3, borderRadius: 2, background: 'rgba(255,255,255,0.20)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${creditPct}%`, background: creditBtnColor, borderRadius: 2, transition: 'width .3s' }} />
          </div>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', whiteSpace: 'nowrap' }}>
            {Math.round(credits).toLocaleString('fr-FR')} cr.
          </span>
        </div>
      </button>

      {/* ── Backdrop ── */}
      {open && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: Z.panelBackdrop, background: 'rgba(0,0,0,0.30)' }}
          onClick={() => setOpen(false)}
        />
      )}

      {/* ── Slide-in panel ── */}
      <div
        style={{
          position: 'fixed', top: 0, right: open ? 0 : -420, bottom: 0,
          // 10001 > 9999 (bulle de support) : une fois OUVERT, ce volet est une
          // surface modale et la bulle ne doit pas flotter par-dessus. Sans
          // danger pour les modales à zIndex 1000 : le volet se ferme lui-même
          // avant de les ouvrir (`setOpen(false)` précède chaque appel).
          width: 400, background: '#fff', zIndex: Z.panel,
          boxShadow: '-4px 0 32px rgba(0,0,0,0.12)',
          transition: 'right 0.28s cubic-bezier(.4,0,.2,1)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: '#111827', margin: 0 }}>Mon espace Qualyse</h3>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="#9CA3AF" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: '18px 24px', overflowY: 'auto' }}>

          {/* User info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', background: '#1B4332', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <User size={20} color="#fff" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                {displayName}
                {isAdmin && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3, background: '#1B4332', color: '#fff', letterSpacing: '0.05em' }}>ADMIN</span>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: isPro ? '#1B4332' : '#6B7280', marginTop: 2 }}>
                Plan&nbsp;: {isPro ? 'Pro ⭐' : 'Gratuit 🆓'}
              </div>
            </div>
          </div>

          {/* Credits card */}
          <div style={{ background: '#F9FAFB', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #F3F4F6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Wallet size={13} color="#1B4332" />
                <span style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', letterSpacing: '0.07em', fontFamily: 'monospace' }}>CRÉDITS</span>
              </div>
              <span style={{ fontSize: 11, color: '#6B7280' }}>/ {maxCredits.toLocaleString('fr-FR')}</span>
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 6 }}>
              {Math.round(credits).toLocaleString('fr-FR')}
            </div>
            <UsageBar value={Math.round(credits)} max={maxCredits} />
            {isPro && expiresAt && (() => {
              const expDate = new Date(expiresAt)
              const isExpired = expDate < new Date()
              const soonMs = 7 * 24 * 3600 * 1000
              const expiresSoon = !isExpired && expDate < new Date(Date.now() + soonMs)
              return (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 11, color: isExpired ? '#DC2626' : expiresSoon ? '#D97706' : '#9CA3AF' }}>
                    {isExpired
                      ? `⚠️ Abonnement expiré le ${expDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
                      : `Expire le ${expDate.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`}
                  </div>
                  {(isExpired || expiresSoon) && (
                    <button
                      onClick={() => { setOpen(false); window.location.href = '/pricing' }}
                      style={{ marginTop: 8, width: '100%', padding: '7px 0', borderRadius: 7, fontSize: 12, fontWeight: 700, color: '#fff', background: '#1B4332', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                      <RefreshCw size={12} />
                      {isExpired ? 'Renouveler mon abonnement' : 'Renouveler maintenant'}
                    </button>
                  )}
                </div>
              )
            })()}
          </div>

          {/* Admin shortcut */}
          {isAdmin && onOpenAdmin && (
            <button
              onClick={() => { onOpenAdmin(); setOpen(false) }}
              style={{ width: '100%', padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', background: '#1B4332', border: 'none', cursor: 'pointer', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}
            >
              🛠️ Administration
            </button>
          )}

          {/* Pro user: all modules unlimited */}
          {isPro && (
            <div style={{ background: '#F0FAF4', borderRadius: 10, padding: '14px 16px', marginBottom: 16, border: '1px solid #c8e6d0' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#1B4332', letterSpacing: '0.07em', marginBottom: 8, fontFamily: 'monospace' }}>
                ACCÈS PRO — TOUS LES MODULES ILLIMITÉS
              </div>
              {['🎙️ Transcription', '✏️ Correction', '🏷️ Codification (illimitée)', '📋 Note Technique', '📊 Rapport d\'analyse'].map(m => (
                <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, fontSize: 13, color: '#111827' }}>
                  <span style={{ color: '#1B4332', fontWeight: 700, fontSize: 11 }}>✅</span>
                  {m}
                </div>
              ))}
            </div>
          )}

          {/* Free plan: per-module usage */}
          {!isPro && (
            <>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', letterSpacing: '0.07em', marginBottom: 10, fontFamily: 'monospace' }}>
                ACCÈS GRATUIT
              </div>

              <ModuleCard emoji="🎙️" title="TRANSCRIPTION & CORRECTION">
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>✅ 1 transcription de 30 min offerte</div>
                <UsageBar value={limits.transcription_count} max={FREE_LIMITS.transcription_count} />
                <div style={{ fontSize: 12, color: '#374151', marginTop: 8, marginBottom: 6 }}>✅ 1 correction verbatim offerte</div>
                <UsageBar value={limits.correction_count} max={FREE_LIMITS.correction_count} />
              </ModuleCard>

              <ModuleCard emoji="🏷️" title="CODIFICATION">
                <div style={{ fontSize: 12, color: '#374151', marginBottom: 6 }}>
                  ✅ {FREE_LIMITS.codification_count} entretiens maximum
                </div>
                <UsageBar value={limits.codification_count} max={FREE_LIMITS.codification_count} />
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
                  {Math.max(0, FREE_LIMITS.codification_count - limits.codification_count)} entretien(s) restant(s)
                </div>
              </ModuleCard>

              <LockedModuleCard emoji="📋" title="NOTE TECHNIQUE" onUpgrade={() => { setOpen(false); openUpgrade() }} />
              <LockedModuleCard emoji="📊" title="RAPPORT D'ANALYSE" onUpgrade={() => { setOpen(false); openUpgrade() }} />

              {/* Pro CTA */}
              <div style={{ background: '#F0FAF4', borderRadius: 10, padding: '14px 16px', marginTop: 8, border: '1px solid #c8e6d0' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#1B4332', letterSpacing: '0.07em', marginBottom: 10, fontFamily: 'monospace' }}>
                  {/* Prix de référence UNIQUE (D-020) — la mention « 20$ » était
                      la faute même relevée par D-019 : une devise qui n'est celle
                      d'aucun marché ouvert. */}
                  PLAN PRO — {REFERENCE_PRICE_LABEL.toUpperCase()}
                </div>
                {['Tous les modules', '1 300 crédits', 'Note Technique', 'Rapport d\'analyse', 'Codification illimitée', 'Support prioritaire'].map(m => (
                  <div key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, fontSize: 12, color: '#111827' }}>
                    <span style={{ color: '#1B4332', fontWeight: 700, fontSize: 11 }}>⭐</span>
                    {m}
                  </div>
                ))}
                {/* Le montant local était FAUX (13 000 au lieu de 13 100) et n'a
                    pas sa place ici : D-020 réserve son affichage au Payment Hub,
                    après le choix du pays et du moyen de paiement. */}
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 8, marginBottom: 10 }}>Montant local affiché au moment du paiement</div>
                <button
                  onClick={() => { setOpen(false); openUpgrade() }}
                  style={{ width: '100%', padding: '10px 0', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', background: '#1B4332', border: 'none', cursor: 'pointer' }}
                >
                  🚀 Choisir le Plan Pro
                </button>
              </div>
            </>
          )}

          {/* Payment history */}
          {paymentHistory.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', letterSpacing: '0.07em', marginBottom: 10, fontFamily: 'monospace' }}>
                HISTORIQUE DES PAIEMENTS
              </div>
              {paymentHistory.map(tx => (
                <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Plan Pro activé</div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>
                      {new Date(tx.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </div>
                  </div>
                  {/* Montant RÉELLEMENT débité, lu sur la transaction. La chaîne
                      précédente — « 20$ · 13 100 FCFA » — était constante DANS la
                      boucle : chaque paiement s'y affichait au même montant, quel
                      qu'ait été le débit. Et la devise était fausse (D-019). */}
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1B4332' }}>
                    {tx.amount.toLocaleString('fr-FR')} {tx.currency}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Recent history */}
          {recentItems.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#6B7280', letterSpacing: '0.07em', marginBottom: 10, fontFamily: 'monospace' }}>
                HISTORIQUE RÉCENT
              </div>
              {recentItems.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
                  <span style={{ fontSize: 14, marginTop: 1 }}>📄</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.file_name}
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>
                      {new Date(item.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid #F3F4F6', display: 'flex', gap: 10, flexShrink: 0 }}>
          <button
            onClick={() => { onOpenDashboard?.(); setOpen(false) }}
            style={{ flex: 1, padding: '9px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#374151', background: '#F9FAFB', border: '1px solid #E5E7EB', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <Settings size={13} />
            Paramètres
          </button>
          <button
            onClick={() => { onLogout(); setOpen(false) }}
            style={{ flex: 1, padding: '9px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          >
            <LogOut size={13} />
            Déconnexion
          </button>
        </div>
      </div>
    </>
  )
}
